import { MediaMatcher } from '@angular/cdk/layout';
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {
  ICliente,
  IFilter,
  IListado,
  ILogIntegracionSoflex,
  IQueryParam,
} from 'modelos/src';
import { Subscription, firstValueFrom } from 'rxjs';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../auxiliares/filtro-tabla/filtro-tabla/filtro-tabla.component';
import { HelperService } from '../../../auxiliares/servicios/helper.service';
import { ListadosService } from '../../../auxiliares/servicios/listados.service';
import { DetalleLogIntegracionSoflexComponent } from '../detalle-log-integracion-soflex/detalle-log-integracion-soflex.component';
import { LogIntegracionSoflexService } from '../log-integracion-soflex.service';

/**
 * Los bodies HTTP, headers y stack traces pueden ser pesados: el listado no los
 * trae y el detalle los pide por id.
 */
const SELECT_LISTADO =
  '-requestBodyHttp -requestHeaders -responseHeaders -stackTrace';

@Component({
  selector: 'app-listado-log-integracion-soflex',
  templateUrl: './listado-log-integracion-soflex.component.html',
  styleUrls: ['./listado-log-integracion-soflex.component.scss'],
  standalone: false,
})
export class ListadoLogIntegracionSoflexComponent implements OnInit, OnDestroy {
  private changeDetectorRef = inject(ChangeDetectorRef);
  private media = inject(MediaMatcher);
  helper = inject(HelperService);
  private listadosService = inject(ListadosService);
  private service = inject(LogIntegracionSoflexService);
  private dialog = inject(MatDialog);

  public loading = false;

  // Filtros
  public search: IRegExpSearch = {
    fields: [
      'requestBody.trackingid',
      'requestBody.deviceid',
      'requestBody.telefono',
      'requestBody.dni',
      'requestBody.label',
      'idConfigVecino',
      'idAlerta',
    ],
  };
  /**
   * Los logs de alerta no traen nombre, DNI ni teléfono en el requestBody, así
   * que la búsqueda libre solo encuentra altas. Filtrando por idConfigVecino se
   * ven las altas y las alertas del vecino.
   */
  public vecino: IFiltroTabla = {
    filter: { field: 'idConfigVecino' },
    label: 'Vecino (nombre, DNI o teléfono)',
    selectLabel: 'etiqueta',
    selectValue: '_id',
    tipo: 'ngselect-async',
    asyncFunction: 'listarConfigVecinosEtiqueta$',
    searchOn: [
      'datosPersonales.nombre',
      'datosPersonales.dni',
      'datosPersonales.telefono',
    ],
  };
  public cliente: IFiltroTabla = {
    elementos: [],
    filter: { field: 'idCliente' },
    label: 'Cliente',
    selectLabel: 'nombre',
    selectValue: '_id',
    tipo: 'select',
  };
  public tipoOperacion: IFiltroTabla = {
    elementos: [
      { nombre: 'Alta de vecino', _id: 'alta_vecino' },
      { nombre: 'Alerta', _id: 'alerta' },
    ],
    filter: { field: 'tipoOperacion' },
    label: 'Operación',
    selectLabel: 'nombre',
    selectValue: '_id',
    tipo: 'select',
  };
  public estado: IFiltroTabla = {
    elementos: [
      { nombre: 'Pendiente', _id: 'pendiente' },
      { nombre: 'Enviando', _id: 'enviando' },
      { nombre: 'Exitoso', _id: 'exitoso' },
      { nombre: 'Error', _id: 'error' },
      { nombre: 'Error (máx. reintentos)', _id: 'error_maximo_reintentos' },
    ],
    filter: { field: 'estado' },
    label: 'Estado',
    selectLabel: 'nombre',
    selectValue: '_id',
    tipo: 'select',
  };
  public fecha: IFiltroTabla = {
    label: 'Rango de Fechas',
    filter: { field: 'fechaCreacion' },
    tipo: 'dateRange',
  };

  // Tabla
  public columnas = [
    'fechaCreacion',
    'cliente',
    'tipoOperacion',
    'metodoAPI',
    'vecino',
    'trackingid',
    'estado',
    'responseStatus',
    'intentos',
    'duracionMs',
    'acciones',
  ];
  public nombreColumnas = [
    'Fecha',
    'Cliente',
    'Operación',
    'Método API',
    'Vecino',
    'Tracking ID',
    'Estado',
    'HTTP',
    'Intentos',
    'Duración',
    'Acciones',
  ];
  public saveColumnas = 'tabla-log-integracion-soflex';
  public dataSource = new MatTableDataSource<ILogIntegracionSoflex>([]);
  public queryParams: IQueryParam = {
    page: 0,
    limit: +this.helper.pageSize,
    sort: '-fechaCreacion',
    select: SELECT_LISTADO,
    filter: JSON.stringify({}),
  };
  public totalCount = 0;

  private clientes$?: Subscription;
  private clientes: ICliente[] = [];
  public mobileQuery: MediaQueryList;

  constructor() {
    this.mobileQuery = this.media.matchMedia('(max-width: 599px)');
    this.mobileQuery.addEventListener('change', () =>
      this.changeDetectorRef.detectChanges(),
    );
  }

  public pageEvent(event: PageEvent): void {
    this.helper.pageEvent(event);
    this.queryParams.page = event.pageIndex;
    this.queryParams.limit = event.pageSize;
    this.actualizar();
  }

  public sortChange(event: Sort): void {
    this.queryParams.sort =
      event.direction === 'asc' ? event.active : `-${event.active}`;
    this.actualizar();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public async cambioFiltro(filters: IFilter<any>) {
    this.queryParams = {
      ...this.queryParams,
      page: 0,
      filter: JSON.stringify(filters),
    };
    await this.actualizar();
  }

  public async actualizar(): Promise<void> {
    this.loading = true;
    try {
      const data = await firstValueFrom(
        this.service.getFiltered(this.queryParams),
      );
      this.totalCount = data.totalCount;
      this.dataSource.data = data.datos;
    } finally {
      this.loading = false;
    }
  }

  public clienteNombre(id?: string): string {
    return this.clientes.find((c) => c._id === id)?.nombre || id || '';
  }

  public verDetalle(row: ILogIntegracionSoflex): void {
    this.dialog.open(DetalleLogIntegracionSoflexComponent, {
      data: { id: row._id, clienteNombre: this.clienteNombre(row.idCliente) },
      width: '1100px',
      maxWidth: '95vw',
    });
  }

  private async listarClientes(): Promise<void> {
    const query: IQueryParam = { select: 'nombre', sort: 'nombre' };
    this.clientes$?.unsubscribe();
    this.clientes$ = this.listadosService
      .subscribe<IListado<ICliente>>('clientes', query)
      .subscribe((data) => {
        this.clientes = data.datos;
        this.cliente.elementos = data.datos;
      });
    await this.listadosService.getLastValue('clientes', query);
  }

  async ngOnInit(): Promise<void> {
    await Promise.all([this.actualizar(), this.listarClientes()]);
  }

  ngOnDestroy(): void {
    this.mobileQuery.removeEventListener('change', () =>
      this.changeDetectorRef.detectChanges(),
    );
    this.clientes$?.unsubscribe();
  }
}
